import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';

import { useAuth } from '../store/auth';
import { TicketStatus } from '../store/ticket';
import { TicketListGetParams } from '../store/ticket/list';
import { useTicketsGet } from './api/tickets';
import { getDataInStorage, setDataInStorage } from '../utils/storage';

const STORAGE_KEY = 'ticketsLastSeen';
const POLL_INTERVAL = 60 * 1000; // milliseconds
const TICKETS_PATH = '/tickets';

const latestOpenTicketParams: Partial<TicketListGetParams> = {
  page: 1,
  pageSize: 1,
  orderByColumn: 'createdAt',
  orderBy: 'DESC',
  status: TicketStatus.OPEN,
};

const getLastSeen = (userId: string): string | undefined => getDataInStorage(STORAGE_KEY)[userId];

/**
 * Returns true when an open ticket has been created since the current admin last visited the Tickets page.
 * Works on the client only: the "last seen" timestamp is kept in localStorage per admin,
 * and the latest open ticket is polled from the existing list endpoint.
 */
export function useNewTicketsIndicator(): boolean {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const userId = user?.id ? String(user.id) : '';
  const ticketsGet = useTicketsGet();
  const [lastSeen, setLastSeen] = useState(() => (userId ? getLastSeen(userId) : undefined));

  const latestCreatedAt = ticketsGet.data?.data?.[0]?.createdAt;
  const isTicketsPage = pathname === TICKETS_PATH || pathname.startsWith(`${TICKETS_PATH}/`);

  // Poll only while the tab is visible, and refresh immediately when the admin returns to it.
  useEffect(() => {
    const load = () => {
      if (document.visibilityState === 'visible') {
        ticketsGet.fetch(latestOpenTicketParams);
      }
    };

    load();

    const interval = setInterval(load, POLL_INTERVAL);

    document.addEventListener('visibilitychange', load);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', load);
    };
  }, [ticketsGet.fetch]);

  useEffect(() => {
    setLastSeen(userId ? getLastSeen(userId) : undefined);
  }, [userId]);

  // Keep several open tabs in sync.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY && userId) {
        setLastSeen(getLastSeen(userId));
      }
    };

    window.addEventListener('storage', onStorage);

    return () => window.removeEventListener('storage', onStorage);
  }, [userId]);

  // Visiting the Tickets page marks everything up to the latest ticket as seen.
  useEffect(() => {
    if (isTicketsPage && userId && latestCreatedAt && latestCreatedAt !== lastSeen) {
      setDataInStorage(STORAGE_KEY, { [userId]: latestCreatedAt }, true);
      setLastSeen(latestCreatedAt);
    }
  }, [isTicketsPage, userId, latestCreatedAt]);

  if (isTicketsPage || !latestCreatedAt) {
    return false;
  }

  return !lastSeen || dayjs(latestCreatedAt).isAfter(lastSeen);
}

export default useNewTicketsIndicator;
